/**
 * First-pass orchestration: audio in, reviewable task out.
 *
 * THE PUBLIC ENTRY POINT, and deliberately nothing more. Neither pipeline is
 * implemented here any more — this file prepares the run, CHOOSES a pipeline,
 * runs exactly one of them, and owns the failure path.
 *
 *   ingestAudio(file)
 *     -> decode (Web Audio)            -> real durationSec
 *     -> createTask + putTaskAudio     -> Task { status: 'new' }
 *     -> runFirstPass(taskId)
 *          -> status 'processing'
 *          -> decode + encode a mono 16 kHz WAV          (shared prologue)
 *          -> is a helper up, and may we use it?
 *               yes -> pipeline/aligned.ts    (measured timestamps)
 *               no  -> legacy/legacyPipeline.ts (Gemini's timestamps, repaired)
 *          -> whichever ran persists its own segments and sets status 'review'
 *
 * THE CHOICE IS THE INTERESTING PART. `runAlignedPipeline` returns `null` to
 * mean "fall through to legacy", and throws when falling through would be
 * dishonest — a cancelled run, or an engine the user picked by name. That
 * asymmetry is deliberate: the DEFAULT engine has to degrade silently because a
 * phone cannot run either helper, while a CHOSEN one must fail loudly. A silent
 * "WhisperX -> legacy" swap once produced six times the clips with no warning.
 *
 * Failures never leave a task stuck in `processing`: the outer catch walks the
 * task back to `review` (if a previous run's segments survived) or `new`, with
 * a human-readable `error`, so the Tasks tab can offer a retry.
 */

import { createTask, getSegmentsByTask, getTask, getTaskAudio, putTaskAudio, updateTask } from '../db';
import type { Task } from '../types';
import { isAlignerAvailable } from './aligner';
import { decodeAudioBlob, encodeForUpload } from './audioSlicer';
import { normalizePlan, planToId, DEFAULT_PLAN, type EnginePlan } from './enginePlan';
import { GeminiError } from './gemini';
import { beginRun, endRun, note, patchRun } from './pipelineLog';
import { runAlignedPipeline } from './pipeline/aligned';
import { PHASE_START, report, throwIfAborted, type ProgressCallback } from './progress';
/*
 * THE ONE EDGE INTO THE QUARANTINE. `services/legacy/` is the fallback
 * pipeline — see its README — and this single line is the only place outside
 * that directory allowed to name a file in it. `ClipQaStats` rides the same
 * line rather than being imported from `legacy/clipQa` directly, which is what
 * keeps "exactly one edge" literally true.
 */
import {
  runLegacyPipeline,
  type ClipQaStats,
  type LegacyPipelineOptions,
} from './legacy/legacyPipeline';

/** Sample rate `encodeForUpload` resamples to. Kept in step with audioSlicer. */
const UPLOAD_SAMPLE_RATE = 16000;

/* ------------------------------------------------------------------ */
/* Progress reporting                                                  */
/* ------------------------------------------------------------------ */

/*
 * The shapes, the phase table and `report` live in `services/progress.ts` so
 * that BOTH pipelines can reach them without importing this module (which would
 * be a cycle, and for `legacy/` an upward edge out of the quarantine too). The
 * three public types are re-exported here, so nothing that imported them from
 * `firstPass` had to change.
 */
export type { FirstPassPhase, FirstPassProgress, ProgressCallback } from './progress';

/* ------------------------------------------------------------------ */
/* Step 1 — bring audio into the app                                   */
/* ------------------------------------------------------------------ */

export interface CreateTaskResult {
  task: Task;
  /** The decoded buffer, handed straight to `runFirstPass` to avoid a re-decode. */
  buffer: AudioBuffer;
}

/** Suggest a task name from a File, or a timestamped one for a recording. */
export function suggestTaskName(file: Blob, fallbackPrefix = 'Recording'): string {
  const name = (file as File).name;
  if (typeof name === 'string' && name.trim()) return name.trim();
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${fallbackPrefix} ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/**
 * Decode the audio, create the Task row and stash the original blob.
 * Does NOT call Gemini — that's {@link runFirstPass}.
 */
export async function createTaskFromAudio(
  file: Blob,
  options: { name?: string; onProgress?: ProgressCallback } = {},
): Promise<CreateTaskResult> {
  const { onProgress } = options;
  report(onProgress, { phase: 'decoding', message: 'Decoding audio…', ratio: PHASE_START.decoding });

  const buffer = await decodeAudioBlob(file);
  const durationSec = buffer.duration;
  if (!Number.isFinite(durationSec) || durationSec <= 0) {
    throw new Error('That audio has no playable length.');
  }

  const task = await createTask({
    name: options.name ?? suggestTaskName(file),
    durationSec,
    status: 'new',
  });
  await putTaskAudio(task.id, file);

  return { task, buffer };
}

/* ------------------------------------------------------------------ */
/* Step 2 — the actual first pass                                      */
/* ------------------------------------------------------------------ */

/**
 * Everything `runFirstPass` accepts.
 *
 * The four QA/grouping knobs are inherited from {@link LegacyPipelineOptions}
 * rather than declared here, because ONLY the legacy pipeline reads them —
 * nothing said so while they sat inline, so choosing the aligned engine
 * silently made all four inert. The public shape is unchanged.
 */
export interface RunFirstPassOptions extends LegacyPipelineOptions {
  onProgress?: ProgressCallback;
  signal?: AbortSignal;
  /** Reuse an already-decoded buffer (saves a full re-decode after upload). */
  buffer?: AudioBuffer;
  /** Seconds of padding added to each side of a clip. Read by both paths. */
  padSec?: number;
  /** Extra prompt context, e.g. "the speaker is a child, language is Arabic". */
  extraInstructions?: string;
  /**
   * Skip the local forced-alignment helper even when it is running, forcing
   * the legacy Gemini-timestamp pipeline. Measurement escape hatch.
   */
  skipAligner?: boolean;
  /** What to run, stage by stage. See services/enginePlan.ts. */
  plan?: EnginePlan;
}

export interface RunFirstPassResult {
  taskId: string;
  segmentCount: number;
  /** What the QA stage did, when it ran. Absent if skipped or it blew up. */
  qa?: ClipQaStats;
}

/**
 * Task ids with a first pass in flight.
 *
 * The Tasks page unmounts on every tab switch, taking its `busy` flag with it,
 * so nothing else stops a user from tapping "Retry first pass" on a task that
 * is already running. Two concurrent runs race on the same segment/clip stores
 * and the loser's clips end up orphaned (or its segments clipless). Module
 * scope, because the guard has to outlive the page.
 */
const inFlight = new Set<string>();

/** Is a first pass currently running for this task? */
export function isFirstPassRunning(taskId: string): boolean {
  return inFlight.has(taskId);
}

/**
 * Transcribe, slice and persist. Safe to re-run on a task that previously
 * failed; existing segments for the task are replaced by the new ones.
 */
export async function runFirstPass(
  taskId: string,
  options: RunFirstPassOptions = {},
): Promise<RunFirstPassResult> {
  const { onProgress, signal } = options;

  const task = await getTask(taskId);
  if (!task) throw new Error('That task no longer exists.');
  if (inFlight.has(taskId)) throw new Error('A first pass is already running for this task.');

  inFlight.add(taskId);
  const run = beginRun(taskId, task.name);

  try {
    await updateTask(taskId, { status: 'processing', error: undefined });

    /* -- decode ---------------------------------------------------- */
    /*
     * The ingest path decodes once in `createTaskFromAudio` and hands the buffer
     * straight here, so the SAME AudioBuffer feeds both the Gemini upload and
     * the slicer. That matters: two independent decodes of the same container
     * could disagree on where sample zero is (encoder delay handling differs),
     * and every clip would be cut against a timeline the model never heard.
     * A re-run without a buffer re-decodes the identical stored blob with the
     * identical decoder, which is the same guarantee by a slower route.
     */
    let buffer = options.buffer;
    const sourceBlob = await getTaskAudio(taskId).catch(() => undefined);
    if (sourceBlob) {
      patchRun({
        source: {
          name: task.name,
          type: sourceBlob.type,
          sizeBytes: sourceBlob.size,
        },
      });
    }
    if (!buffer) {
      report(onProgress, { phase: 'decoding', message: 'Decoding audio…', ratio: PHASE_START.decoding });
      if (!sourceBlob) throw new Error('The original audio for this task is missing.');
      buffer = await decodeAudioBlob(sourceBlob);
      note('re-decoded from the stored blob (no buffer handed in)');
    }
    throwIfAborted(signal);

    const durationSec = buffer.duration;
    patchRun({
      decoded: {
        sampleRate: buffer.sampleRate,
        channels: buffer.numberOfChannels,
        durationSec,
        lengthSamples: buffer.length,
      },
    });
    if (Math.abs(durationSec - task.durationSec) > 0.05) {
      await updateTask(taskId, { durationSec });
      note(`task duration corrected ${task.durationSec.toFixed(2)}s -> ${durationSec.toFixed(2)}s`);
    }

    /* -- prepare the upload ---------------------------------------- */
    report(onProgress, {
      phase: 'preparing',
      message: 'Preparing audio for Gemini…',
      ratio: PHASE_START.preparing,
    });
    const uploadBlob = encodeForUpload(buffer);
    patchRun({
      upload: {
        mimeType: 'audio/wav',
        sampleRate: UPLOAD_SAMPLE_RATE,
        channels: 1,
        bitsPerSample: 16,
        sizeBytes: uploadBlob.size,
      },
    });
    throwIfAborted(signal);

    /* -- ALIGNED PATH (preferred) ----------------------------------- */
    /*
     * When the local forced-alignment helper is running, Gemini is only asked
     * WHAT was said; WHERE comes from the aligner's physical measurement and
     * the VAD fills in noise/unclear/silence so the whole timeline is labeled.
     * The legacy timestamp-repair pipeline below stays as the fallback (it is
     * also what phones use — they cannot run the helper).
     */
    const plan = normalizePlan(options.plan ?? DEFAULT_PLAN);
    const engine = planToId(plan);
    /*
     * An EXPLICITLY chosen engine is a promise, not a preference. Falling back
     * silently is how a "WhisperX -> Gemini" run turned into the legacy
     * Gemini-cuts-everything pipeline and produced six times the clips with no
     * warning. So: default engine may fall back (phones cannot run helpers);
     * a chosen one fails loudly instead.
     */
    const explicit = plan.base === 'whisperx';
    const engineReady = explicit
      ? await (await import('./whisperxClient')).isWhisperxAvailable(6000)
      : await isAlignerAvailable();
    if (explicit && !engineReady) {
      throw new Error(
        'The WhisperX helper is not responding, so this engine cannot run. Start it (see README) or pick another engine — the run was stopped rather than quietly using a different pipeline.',
      );
    }
    if (!options.skipAligner && engineReady) {
      /*
       * `runAlignedPipeline` resolves to null when it failed in a way that is
       * allowed to degrade to the legacy pipeline, and throws when it is not
       * (an abort, or an engine the user chose explicitly). The three rules are
       * documented on it — they are the part that must not drift.
       */
      const aligned = await runAlignedPipeline({
        taskId,
        buffer,
        uploadBlob,
        plan,
        engine,
        explicit,
        signal,
        onProgress,
        padSec: options.padSec,
      });
      if (aligned) return aligned;
    }

    /* -- LEGACY PATH (the fallback, and what phones always take) ----- */
    /*
     * Gemini invents the timestamps here and the whole repair tower after it
     * exists to fix them. Lifted verbatim into services/legacy/legacyPipeline.ts
     * — it persists the segments, writes the task back to `review` and closes
     * the run log itself, exactly as it did inline.
     */
    return await runLegacyPipeline({
      taskId,
      buffer,
      uploadBlob,
      durationSec,
      run,
      signal,
      onProgress,
      padSec: options.padSec,
      extraInstructions: options.extraInstructions,
      skipQa: options.skipQa,
      qaConcurrency: options.qaConcurrency,
      qaBudgetMs: options.qaBudgetMs,
      skipGrouping: options.skipGrouping,
    });
  } catch (err) {
    const message = describeError(err);
    // Before anything else: a failed run's log is the one most worth keeping.
    await endRun('error', message).catch(() => undefined);
    // A failed RE-run keeps the segments of the previous, good run, so the task
    // must go back to `review` — parking it in `new` while it still holds a
    // reviewable segment set gives it two contradictory affordances ("run the
    // first pass" on the Tasks tab, its clips still queued in Verify).
    const survivors = (await getSegmentsByTask(taskId).catch(() => [])).length;
    await updateTask(taskId, { status: survivors > 0 ? 'review' : 'new', error: message });
    report(onProgress, { phase: 'error', message, ratio: 1 });
    throw err instanceof Error ? err : new Error(message);
  } finally {
    inFlight.delete(taskId);
  }
}

/* ------------------------------------------------------------------ */
/* Step 1 + 2 — what the Tasks tab actually calls                      */
/* ------------------------------------------------------------------ */

export interface IngestOptions extends Omit<RunFirstPassOptions, 'buffer'> {
  name?: string;
  /** Create the task but stop before calling Gemini. Default false. */
  skipFirstPass?: boolean;
  /** Called as soon as the Task row exists, before the slow work starts. */
  onTaskCreated?: (task: Task) => void;
}

/** Upload/record -> task -> first pass, in one call. */
export async function ingestAudio(file: Blob, options: IngestOptions = {}): Promise<Task> {
  const { task, buffer } = await createTaskFromAudio(file, {
    name: options.name,
    onProgress: options.onProgress,
  });
  options.onTaskCreated?.(task);

  if (options.skipFirstPass) return task;

  /*
   * SPREAD, not a hand-written field list.
   *
   * This was nine fields copied one by one, and `plan` was missing from it —
   * so the engine toggle silently did nothing and every upload ran the default
   * pipeline. A spread cannot forget a field, which is the only reason this
   * line is written this way. `IngestOptions` is `RunFirstPassOptions` plus
   * three ingest-only keys, and `runFirstPass` ignores keys it does not know,
   * so passing them through is harmless; `buffer` is set last on purpose,
   * because the caller may not override the buffer we just decoded.
   */
  await runFirstPass(task.id, { ...options, buffer });

  return (await getTask(task.id)) ?? task;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Turn anything thrown in the pipeline into a sentence a human can act on. */
export function describeError(err: unknown): string {
  if (err instanceof GeminiError) return err.message;
  if (err instanceof Error) {
    // Storing hundreds of clip WAVs is the realistic way to run out of room,
    // and the raw DOMException ("The quota has been exceeded.") gives the user
    // nothing to act on.
    if (err.name === 'QuotaExceededError' || /quota/i.test(err.message)) {
      return 'Out of browser storage. Delete a finished task to free space, then try again.';
    }
    return err.message || 'Something went wrong during the first pass.';
  }
  return String(err ?? 'Unknown error');
}
